import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { conciliacaoService } from '../services/conciliacaoService';
import type { AjustarDivergenciaDto, FecharConciliacaoDto } from '../types/pdv.types';

export const conciliacaoKeys = {
  auditoria: (empresaId: number, dtInicio: string, dtFim: string) =>
    ['conciliacao', 'auditoria', empresaId, dtInicio, dtFim] as const,
  relatorio: (empresaId: number, dtInicio: string, dtFim: string, turnoId?: number) =>
    ['conciliacao', 'relatorio', empresaId, dtInicio, dtFim, turnoId] as const,
  historico: (turnoId: number) =>
    ['conciliacao', 'historico', turnoId] as const,
};

export function useAuditoriaPeriodo(
  empresaId: number,
  dtInicio: string,
  dtFim: string,
  enabled = true,
) {
  return useQuery({
    queryKey: conciliacaoKeys.auditoria(empresaId, dtInicio, dtFim),
    queryFn: () => conciliacaoService.auditarPeriodo(empresaId, dtInicio, dtFim),
    enabled: enabled && !!empresaId && !!dtInicio && !!dtFim,
  });
}

export function useRelatorioConciliacao(
  empresaId: number,
  dtInicio: string,
  dtFim: string,
  turnoId?: number,
  enabled = true,
) {
  return useQuery({
    queryKey: conciliacaoKeys.relatorio(empresaId, dtInicio, dtFim, turnoId),
    queryFn: () => conciliacaoService.gerarRelatorio(empresaId, dtInicio, dtFim, turnoId),
    enabled: enabled && !!empresaId && !!dtInicio && !!dtFim,
  });
}

export function useHistoricoAjustes(turnoId: number | undefined) {
  return useQuery({
    queryKey: conciliacaoKeys.historico(turnoId ?? 0),
    queryFn: () => conciliacaoService.obterHistoricoAjustes(turnoId!),
    enabled: !!turnoId,
  });
}

export function useAjustarDivergencia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ turnoId, dto }: { turnoId: number; dto: AjustarDivergenciaDto }) =>
      conciliacaoService.ajustarDivergencia(turnoId, dto),
    onSuccess: (_, { turnoId }) => {
      qc.invalidateQueries({ queryKey: ['conciliacao', 'historico', turnoId] });
      qc.invalidateQueries({ queryKey: ['conciliacao', 'auditoria'] });
    },
  });
}

export function useFecharConciliacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ turnoId, dto }: { turnoId: number; dto: FecharConciliacaoDto }) =>
      conciliacaoService.fecharConciliacao(turnoId, dto),
    onSuccess: (_, { turnoId }) => {
      qc.invalidateQueries({ queryKey: ['conciliacao', 'historico', turnoId] });
      qc.invalidateQueries({ queryKey: ['conciliacao', 'auditoria'] });
    },
  });
}
